import { formatQualityLabel } from '@omni/kit/components/KitPlayer/utils/formatQualityLabel';
import Colors from '@omni/kit/theming/Colors';
import Hls, { MediaPlaylist } from 'hls.js';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';

import getUserAgent from '../../utils/getUserAgent';
import {
  PlayerVideoProps,
  PlayerVideoRef,
  QualityLevel,
} from './PlayerVideo.types';
import useCachedVolume from './hooks/useCachedVolume';
import useEventListeners from './hooks/useEventListeners.web';
import useFullscreenWeb from './hooks/useFullscreen.web';
import useHlsEvents from './hooks/useHlsEvents.web';
import { setSubtitleLanguage } from './util/cachedSubtitle';
import { setCachedMute, setCachedVolume } from './util/cachedVolume';
import { getFileExtensionFromUrl } from './util/getFileExtension';

const debug = require('debug')('tca:KitPlayer:components:PlayerVideo');
const PROGRESS_INTEVAL = 1000; // 1 second
const UPDATE_CURRENT_DURATION_INTERVAL = 1000; // 1 second
const MIN_MAX_BUFFER_LENGTH = 1; // 1 second to avoid excessive buffering before media plays

const HLS_EXTENSION = 'm3u8';

const PlayerVideo = React.forwardRef<PlayerVideoRef, PlayerVideoProps>(
  (props, playerVideoRef) => {
    const {
      containerRef,
      uri = '',
      playing = true,
      playbackRate = 1,
      mediaType = 'video',
      muted = false,
      style,
      controlOverrides = {},
      isLive = false,

      onBufferStart,
      onBufferEnd,
      onDurationChange,
      onError,
      onPause,
      onPlaybackRateChange,
      onProgress,
      onSeek,
      onLiveDetected,
      onSeeked,
      onSeeking,
    } = props;

    const currentDuration = useRef(0);
    const totalDuration = useRef(0);
    const currentDurationIntervalId = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    const [isMute, setIsMute] = useState(muted);

    const playerRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const subtitleTracksRef = useRef<MediaPlaylist[]>([]);
    const qualityLevelsRef = useRef<QualityLevel[]>([]);
    const isManuallySwitchingLevelsRef = useRef<boolean>(false);
    const currentQualityLevelRef = useRef<number>(-1);
    const currentQualityLabelRef = useRef<string>('');

    const pauseMedia = useCallback(() => {
      playerRef.current?.pause();
    }, []);

    const playMedia = useCallback(async () => {
      try {
        // Check if we need to buffer before playing
        const player = playerRef.current;

        if (player) {
          const buffered = player.buffered;
          let isBufferedAhead = false;

          // Check if current position is buffered
          if (buffered.length > 0) {
            isBufferedAhead = true;
          }

          // If not buffered enough, trigger buffer start immediately
          if (!isBufferedAhead) {
            debug('Not enough buffer, triggering onBufferStart');
            onBufferStart?.();
          }
        }

        await playerRef.current?.play();
      } catch (e) {
        debug('playMedia error', e);
        onPause?.();
      }
    }, [onPause, onBufferStart]);

    const {
      hasFullScreenSupport,
      presentFullscreenPlayer,
      dismissFullscreenPlayer,
    } = useFullscreenWeb({
      containerRef,
      playerRef,
    });

    const { isReady, isPlaying } = useEventListeners({
      ...props,
      hlsRef,
      pauseMedia,
      playerRef,
      playMedia,
      onSeeked,
      onSeeking,
    });

    const { attachHlsEvents } = useHlsEvents({
      onError,
      onBufferStart,
      onBufferEnd,
      onLiveDetected,
      subtitleTracksRef,
      qualityLevelsRef,
      isManuallySwitchingLevelsRef,
      currentQualityLevelRef,
      currentQualityLabelRef,
    });

    const volumeControlEnabled = useMemo(() => {
      return controlOverrides?.volume !== false;
    }, [controlOverrides?.volume]);

    const getSecondsLoaded = useCallback(() => {
      const player = playerRef.current;

      if (!player) return null;

      const { buffered } = player;

      if (buffered.length === 0) {
        return 0;
      }

      const end = buffered.end(buffered.length - 1);
      const duration = player.duration;

      return end > duration ? duration : end;
    }, []);

    const getLatestSeekableTime = useCallback(() => {
      const player = playerRef.current;

      if (!player) return 0;

      const { seekable } = player;

      if (!seekable || seekable.length === 0) {
        /**
         * TODO: figure out if this is needed?
         * seekable.end(seekable.length - 1) will always
         * throw an error if attempts to access seekable.end(-1)
         */
        try {
          const liveEdge = seekable.end(seekable.length - 1);

          if (Number.isFinite(liveEdge)) return liveEdge;
        } catch (error) {
          debug('Failed to get latest seekable time:', error);
        }
      }

      const sync = hlsRef.current?.liveSyncPosition;

      if (Number.isFinite(sync)) return sync!;

      if (player.buffered?.length) {
        return player.buffered.end(player.buffered.length - 1);
      }

      return 8 * 60 * 60 * 1000; //8H in milliseconds;
    }, []);

    const updateCurrentDuration = useCallback(
      (newDuration: number) => {
        currentDuration.current = newDuration;

        onDurationChange?.(currentDuration.current);
      },
      [onDurationChange]
    );

    /**
     * Increment currentDuration by x seconds every second until it reaches totalDuration
     * and the logic to increment is based on the timeDifference between current and total duration
     *
     * Notes:
     * This smoothes out the incoming buffer increasing the total duration by 10 seconds every 10 seconds (approx)
     * for live streaming
     */
    const updateCurrentDurationOverTime = useCallback(() => {
      // Clear any existing timer
      if (currentDurationIntervalId.current) {
        clearInterval(currentDurationIntervalId.current);
      }

      // Only do this if it's truly live, the player is ready,
      // and totalDuration is known
      if (!isLive || !totalDuration.current) {
        return;
      }

      const step = () => {
        // Calculate the timeDifference between current and total duration
        const timeDifference = totalDuration.current - currentDuration.current;

        if (Number.isFinite(timeDifference) && timeDifference > 0) {
          /**
           * Adaptive increment strategy:
           * - For small gaps (< 5s): increment by 1s (normal speed)
           * - For medium gaps (5-20s): increment by 2s (catch up moderately)
           * - For large gaps (> 20s): increment by timeDifference/10 (catch up faster)
           */
          let increment = 1;

          if (timeDifference > 5 && timeDifference <= 20) {
            increment = 2;
          } else if (timeDifference > 20) {
            // Increment by 10% of the timeDifference, but cap at 10s to avoid huge jumps
            increment = Math.min(timeDifference / 10, 10);
          }

          // Ensure we don't exceed totalDuration
          if (currentDuration.current > totalDuration.current) {
            updateCurrentDuration(totalDuration.current);
          } else {
            updateCurrentDuration(currentDuration.current + increment);
          }
        } else {
          if (currentDurationIntervalId.current) {
            clearInterval(currentDurationIntervalId.current);
          }
        }
      };

      step();

      currentDurationIntervalId.current = setInterval(
        step,
        UPDATE_CURRENT_DURATION_INTERVAL
      );
    }, [isLive, updateCurrentDuration]);

    const getProgress = useCallback(() => {
      if (!isReady) return;

      const player = playerRef.current;

      if (!player) return;

      let playedSeconds = player.currentTime || 0;
      const latestSeekableTime = getLatestSeekableTime();

      // Handle Safari returning -1 for currentTime in live streams
      if (playedSeconds === -1 && isLive) {
        playedSeconds = Number.isFinite(latestSeekableTime)
          ? latestSeekableTime
          : 0;
      }

      const loadedSeconds = getSecondsLoaded();

      onProgress?.({
        currentSeconds: playedSeconds,
        loadedSeconds: loadedSeconds || 0,
      });

      /**
       * Safari duration will be Infinity for live streams
       * so we can use the latestSeekableTime to set the totalDuration
       * and then use the increment logic to update the currentDuration
       */
      if (isLive && player.duration === Infinity) {
        totalDuration.current = latestSeekableTime;

        /** Default currentDuration to latestSeekableTime if it's Infinity */
        if (currentDuration.current === Infinity) {
          updateCurrentDuration(latestSeekableTime);
        }

        if (currentDuration.current > latestSeekableTime) {
          updateCurrentDuration(latestSeekableTime);
        }

        updateCurrentDurationOverTime();
      }
    }, [
      getSecondsLoaded,
      onProgress,
      isReady,
      isLive,
      getLatestSeekableTime,
      updateCurrentDurationOverTime,
      updateCurrentDuration,
    ]);

    const seekTo = useCallback(
      (timeInSeconds: number) => {
        if (playerRef.current) {
          playerRef.current.currentTime = timeInSeconds;
          onSeek?.(timeInSeconds);
        }
      },
      [onSeek]
    );

    const setVolume = useCallback(
      (volume: number) => {
        if (!playerRef?.current) return;

        playerRef.current.volume = volume;
        setCachedVolume(volume);
      },
      /** mediaType is needed in the dependency to ensure volume is set from cache when switching audio/video mode */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [mediaType]
    );

    const setMute = useCallback(
      (mute: boolean) => {
        if (!playerRef.current) return;

        setIsMute(mute);
        playerRef.current.muted = mute;
        setCachedMute(mute);
      },
      /** mediaType is needed in the dependency to ensure volume is set from cache when switching audio/video mode */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [mediaType]
    );

    useCachedVolume({
      onUpdateMute: setMute,
      onUpdateVolume: setVolume,
      volumeEnabled: volumeControlEnabled,
    });

    const updateLive = useCallback(
      (isLive: boolean) => {
        onLiveDetected?.(isLive);
      },
      [onLiveDetected]
    );

    const { name: browserName } = useMemo(() => getUserAgent() || {}, []);

    useEffect(() => {
      if (uri) {
        const player = playerRef.current;

        if (!player) return;

        debug('load uri', uri);

        const fileExtension = getFileExtensionFromUrl(uri);
        const isHlsStream = fileExtension === HLS_EXTENSION;
        const noFileExtension = fileExtension === '';

        /**
         * Related ticket https://subsplash.atlassian.net/browse/MED-11294
         *
         * Hls.js can only play using the video tag and only if:
         * - files with m3u8 extension
         * - streams with redirect to m3u8 (example: https://watch.sardius.media/asset/8d5F99Ed22aB004/pid/np-full-060125)
         *
         * https://github.com/video-dev/hls.js?tab=readme-ov-file
         */
        const isHlsJsCompatible =
          mediaType === 'video' && (isHlsStream || noFileExtension);

        const handleNativePlayback = () => {
          debug('Use native playback', uri);
          player.src = uri;
          player.onloadedmetadata = () => {
            updateLive(player.duration === Infinity);
          };
        };

        const handleHlsPlayback = () => {
          debug('Use hls playback', uri);

          const params = new URLSearchParams(window.location.search);
          const debugEnabled = params.has('debugHls');

          const hls = new Hls({
            maxMaxBufferLength: MIN_MAX_BUFFER_LENGTH,
            startLevel: -1,
            debug: debugEnabled,
          });
          hlsRef.current = hls;
          const removeHlsEvents = attachHlsEvents(hls);
          hls.attachMedia(player);
          hls.loadSource(uri);

          return () => {
            hlsRef.current = null;
            removeHlsEvents();
            hls.detachMedia();
            hls.stopLoad();
            hls.destroy();
          };
        };

        const isSafari = Boolean(
          browserName === 'Safari' || browserName === 'Mobile Safari'
        );

        /**
         * Although chrome support hls natively, it breaks live streaming for
         * chrome version 135
         */
        if (
          isLive &&
          player.canPlayType('application/vnd.apple.mpegurl') &&
          isSafari
        ) {
          /**
           * iOS natively supports hls playback which is preferred for live
           * streams. It is way more performant than using hls.js.
           * https://github.com/video-dev/hls.js/pull/2954#issuecomment-670021358
           *
           * Note:
           * This check is necessary since `Hls.isSupported()` will return true
           * for ios version greater than 18
           */
          debug('Supports native hls playback');
          handleNativePlayback();
        } else if (Hls.isSupported() && isHlsJsCompatible) {
          /**
           * Prefer to use hls.js for vod for ios and android in order to
           * support additional settings like quality levels, subtitles, etc.
           * Using iOS native hls would not be able to support these settings.
           */
          debug('Supports hls playback');

          return handleHlsPlayback();
        } else {
          debug('Fallback to native playback');
          handleNativePlayback();
        }

        // Maintain current time when switching media types
        const currentTime = player.currentTime;
        player.currentTime = currentTime;
      }
    }, [uri, attachHlsEvents, updateLive, isLive, browserName, mediaType]);

    useEffect(() => {
      let pid: ReturnType<typeof setTimeout> | null = null;

      /** trigger getProgress to update the currentDuration */
      getProgress();

      pid = setInterval(getProgress, PROGRESS_INTEVAL);

      return () => {
        if (pid) clearInterval(pid);
      };
    }, [getProgress]);

    useEffect(() => {
      if (!playerRef.current) return;

      if (playing) {
        if (!isPlaying) playMedia();
      } else {
        if (isPlaying) pauseMedia();
      }
    }, [isPlaying, playing, playMedia, pauseMedia]);

    useEffect(() => {
      if (!playerRef.current) return;

      try {
        playerRef.current.playbackRate = playbackRate;
        onPlaybackRateChange?.(playbackRate);
      } catch (error) {
        onError?.({ error: 'PlayerVideo.web: playbackRate error' });
      }
    }, [playbackRate, onPlaybackRateChange, onError, mediaType]);

    useEffect(() => {
      if (!isReady) return;

      const updateDuration = () => {
        const player = playerRef.current;

        if (!player) return;

        let duration = player.duration;

        if (isNaN(duration)) {
          duration = 0;
        }

        totalDuration.current = duration;

        if (isLive) {
          if (
            currentDuration.current === 0 ||
            currentDuration.current === Infinity
          ) {
            // For live streams, use the end of the seekable range as the current duration
            const lastSeekableTime = getLatestSeekableTime();

            const newDuration = Number.isFinite(duration)
              ? duration
              : lastSeekableTime;
            updateCurrentDuration(newDuration);
          }

          updateCurrentDurationOverTime();
        } else {
          /**
           * For on-demand: Always set currentDuration to the actual duration.
           * This ensures on-demand gets updated if the duration changes from 0 to a real number
           */
          updateCurrentDuration(duration);
        }
      };

      const player = playerRef.current;

      if (player) {
        player.addEventListener('durationchange', updateDuration);
        updateDuration();

        return () => {
          player.removeEventListener('durationchange', updateDuration);
        };
      }
    }, [
      isLive,
      isReady,
      updateCurrentDuration,
      updateCurrentDurationOverTime,
      getLatestSeekableTime,
    ]);

    useImperativeHandle(
      playerVideoRef,
      () => ({
        seekTo(timeInSeconds) {
          seekTo(timeInSeconds);
        },
        getLatestSeekableTime() {
          return getLatestSeekableTime();
        },
        seekToLatestBuffer() {
          const latestBufferTime = getSecondsLoaded();

          if (latestBufferTime && latestBufferTime > 0) {
            seekTo(latestBufferTime);
          }
        },
        getSecondsLoaded() {
          return getSecondsLoaded() ?? -1;
        },
        hasFullScreenSupport,
        presentFullscreenPlayer,
        dismissFullscreenPlayer,
        getCurrentTimeInSeconds: () => {
          const player = playerRef.current;

          if (!player) return -1;

          const currentTime = player.currentTime;

          // Handle Safari returning -1, Infinity, or NaN for currentTime in live streams
          if ((currentTime === -1 || !Number.isFinite(currentTime)) && isLive) {
            const lastSeekableTime = getLatestSeekableTime();

            return Number.isFinite(lastSeekableTime) ? lastSeekableTime : 0;
          } else {
            return currentTime;
          }
        },
        getTotalDuration() {
          // Return effective duration if it's set and we're in a live stream, otherwise return total duration
          return isLive ? currentDuration.current : totalDuration.current;
        },
        play() {
          playMedia();
        },
        pause() {
          pauseMedia();
        },
        selectSubtitleTrack(trackIndex = -1) {
          if (!hlsRef.current) return;

          hlsRef.current.subtitleTrack = trackIndex;

          /** cache selected subtitle language */
          const subtitleTrack = subtitleTracksRef.current[trackIndex] || {};
          const lang = subtitleTrack.lang || '';

          setSubtitleLanguage(lang);
        },
        getSubtitleTracks() {
          return subtitleTracksRef.current;
        },
        getVolume: () => playerRef.current?.volume ?? 1,
        setVolume,
        setMute,
        getQualityLevels() {
          return qualityLevelsRef.current;
        },
        getCurrentQualityLabel() {
          return currentQualityLabelRef.current;
        },
        getCurrentQualityLevel() {
          return currentQualityLevelRef.current;
        },
        selectQualityLevel(levelIndex: number) {
          if (!hlsRef.current) return;

          debug('Setting quality level to:', levelIndex);
          isManuallySwitchingLevelsRef.current = true;

          // Enable auto quality mode
          if (levelIndex === -1 && !hlsRef.current.autoLevelEnabled) {
            hlsRef.current.currentLevel = levelIndex;
          } else if (
            levelIndex !== -1 &&
            levelIndex !== hlsRef.current.currentLevel
          ) {
            hlsRef.current.currentLevel = levelIndex;
          } else if (levelIndex !== -1 && hlsRef.current.autoLevelEnabled) {
            hlsRef.current.currentLevel = levelIndex;
          }

          if (levelIndex === -1) {
            currentQualityLabelRef.current = `Auto • ${formatQualityLabel(
              hlsRef.current,
              hlsRef.current.currentLevel
            )}`;
          } else {
            currentQualityLabelRef.current = formatQualityLabel(
              hlsRef.current,
              levelIndex
            );
          }

          currentQualityLevelRef.current = levelIndex;
        },
        isAutoQualityEnabled() {
          return hlsRef.current?.autoLevelEnabled ?? false;
        },
      }),
      [
        hasFullScreenSupport,
        presentFullscreenPlayer,
        dismissFullscreenPlayer,
        setVolume,
        setMute,
        seekTo,
        getSecondsLoaded,
        totalDuration,
        playMedia,
        pauseMedia,
        isLive,
        getLatestSeekableTime,
      ]
    );

    return (
      <View style={[styles.playerContainer, style]}>
        {uri ? (
          mediaType === 'audio' ? (
            <audio
              style={{
                width: '100%',
                backgroundColor: Colors.N1000,
              }}
              ref={playerRef as React.RefObject<HTMLAudioElement>}
              muted={isMute}
              autoPlay={playing}
              controls={false}
              preload='auto'
            />
          ) : (
            <video
              style={{
                height: '100%',
                width: '100%',
                backgroundColor: Colors.N1000,
              }}
              ref={playerRef as React.RefObject<HTMLVideoElement>}
              muted={isMute}
              autoPlay={playing}
              controls={false}
              preload='auto'
              playsInline /** Removes ios-web native player controls */
            />
          )
        ) : null}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  playerContainer: {
    height: '100%',
    position: 'relative',
    width: '100%',
  },
});

export default PlayerVideo;
